/**
 * Precinct entity index page with state/county dropdowns + GEOID search
 *
 * State dropdown filters precincts, county dropdown narrows by county
 * (loaded dynamically when state is selected), search box provides
 * GEOID prefix search. Displays vote totals and margin information.
 */

import { canonicalPath } from '@electinfo/common';
import { countyGeoid, type CountyFips } from './county-geoid';
import { matchLeafDetailPath } from './leaf-detail';
import { loadPrecinctDetail } from './leaf-profiles';
import { belowHeaderInsertPoint } from './page-header';

const PCT_PAGE_SIZE = 50;

/** Fixed document (persisted-query list, sites#277); limit 50 = page size. */
const PRECINCTS_QUERY = `
query Precincts($state: String, $county: String, $search: String, $offset: Int!) {
  precincts(
    where: { state: { eq: $state }, county_fips: { eq: $county }, geoid: { contains: $search } }
    sort: [{ geoid: ASC }]
    limit: 50
    offset: $offset
  ) {
    id
    geoid
    state
    state_fips
    county_fips
    precinct_id
    votes_dem
    votes_rep
    votes_total
    pct_dem_lead
    official_boundary
  }
  precinctsConnection(where: { state: { eq: $state }, county_fips: { eq: $county }, geoid: { contains: $search } }) {
    totalCount
  }
}
`;

interface GraphQLRequest {
  query: string;
  variables: Record<string, unknown>;
}

interface PctStateInfo {
  name: string | null;
  state: string;
}

interface PctCountyInfo extends CountyFips {
  name?: string;
  state?: string;
}

interface PctPrecinctResult {
  id: string;
  geoid: string;
  state?: string;
  state_fips?: string;
  county_fips?: string;
  precinct_id?: string;
  votes_dem?: number;
  votes_rep?: number;
  votes_total?: number;
  pct_dem_lead?: number;
  official_boundary?: boolean;
}

interface PctGraphQLResponse {
  data?: {
    precincts: PctPrecinctResult[];
    precinctsConnection?: {
      totalCount: number;
    };
  };
  errors?: Array<{ message: string }>;
}

class PrecinctIndex {
  private currentState = '';
  private currentCounty = '';
  private currentQuery = '';
  private currentPage = 1;
  private totalResults = 0;
  private states: PctStateInfo[] = [];
  private counties: PctCountyInfo[] = [];

  constructor() {
    this.init();
  }

  private init(): void {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  private async setup(): Promise<void> {
    const detailId = matchLeafDetailPath('precincts');
    if (detailId) {
      await loadPrecinctDetail(detailId);
      return;
    }

    this.injectStyles();
    await this.loadStates();
    this.insertUI();
    this.loadFromURL();
  }

  private async loadStates(): Promise<void> {
    try {
      const res = await fetch('/api/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: '{ states(sort: [{ name: ASC }]) { name state } }'
        })
      });

      if (res.ok) {
        const data = await res.json();
        this.states = (data.data?.states || []).filter((s: PctStateInfo) => s.name);
      }
    } catch (err) {
      console.error('Failed to load states:', err);
    }
  }

  private getStateName(code: string): string {
    const state = this.states.find(s => s.state === code);
    return state?.name || code;
  }

  private getCountyName(geoid: string): string {
    const county = this.counties.find(c => countyGeoid(c) === geoid);
    return county?.name || geoid;
  }

  private async loadCounties(stateCode: string): Promise<void> {
    const countySelect = document.getElementById('precinct-county-select') as HTMLSelectElement;
    if (!countySelect) return;

    if (!stateCode) {
      this.counties = [];
      countySelect.innerHTML = '<option value="">All Counties</option>';
      countySelect.disabled = true;
      return;
    }

    countySelect.innerHTML = '<option value="">Loading counties...</option>';
    countySelect.disabled = true;

    try {
      const res = await fetch('/api/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: `query StateCounties($state: String!) { counties(where: { state: { eq: $state } }, sort: [{ name: ASC }], limit: 500) { name state state_fips county_fips } }`,
          variables: { state: stateCode }
        })
      });

      if (res.ok) {
        const data = await res.json();
        this.counties = (data.data?.counties || []).filter((c: PctCountyInfo) => c.name);
      }
    } catch (err) {
      console.error('Failed to load counties:', err);
      this.counties = [];
    }

    const countyOptions = this.counties
      .map(c => `<option value="${countyGeoid(c)}">${c.name} County</option>`)
      .join('');
    countySelect.innerHTML = `<option value="">All Counties</option>${countyOptions}`;
    countySelect.disabled = false;
  }

  private injectStyles(): void {
    const style = document.createElement('style');
    style.textContent = `
      .precinct-container {
        background: white;
        padding: 20px;
        border-radius: 8px;
        margin-bottom: 20px;
        border: 1px solid var(--border, #e2e8f0);
      }
      .precinct-filters {
        display: flex;
        gap: 10px;
        align-items: center;
        flex-wrap: wrap;
        margin-bottom: 15px;
      }
      .precinct-filters label {
        font-weight: 600;
        color: var(--primary, #1a365d);
      }
      .precinct-filters select {
        padding: 12px 16px;
        font-size: 1rem;
        border: 2px solid var(--border, #e2e8f0);
        border-radius: 6px;
        font-family: inherit;
        min-width: 200px;
        cursor: pointer;
      }
      .precinct-filters select:focus {
        outline: none;
        border-color: var(--secondary, #2c5282);
      }
      .precinct-search-form {
        display: flex;
        gap: 10px;
        flex: 1;
        min-width: 250px;
      }
      .precinct-search-form input {
        flex: 1;
        padding: 12px 16px;
        font-size: 1rem;
        border: 2px solid var(--border, #e2e8f0);
        border-radius: 6px;
        font-family: inherit;
      }
      .precinct-search-form input:focus {
        outline: none;
        border-color: var(--secondary, #2c5282);
      }
      .precinct-search-form button {
        padding: 12px 24px;
        font-size: 1rem;
        background: var(--secondary, #2c5282);
        color: white;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        font-family: inherit;
      }
      .precinct-search-form button:hover {
        background: var(--primary, #1a365d);
      }
      .precinct-results {
        margin-top: 15px;
      }
      .precinct-results-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 15px;
        padding-bottom: 10px;
        border-bottom: 1px solid var(--border, #e2e8f0);
      }
      .precinct-results-header h3 {
        margin: 0;
        color: var(--primary, #1a365d);
      }
      .precinct-clear {
        background: none;
        border: 1px solid var(--border, #e2e8f0);
        padding: 6px 12px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 0.9em;
      }
      .precinct-clear:hover {
        background: var(--bg, #f7fafc);
      }
      .precinct-result {
        display: block;
        padding: 12px 16px;
        border: 1px solid var(--border, #e2e8f0);
        border-radius: 6px;
        margin-bottom: 8px;
        text-decoration: none;
        color: inherit;
      }
      .precinct-result:hover {
        background: var(--bg, #f7fafc);
        border-color: var(--secondary, #2c5282);
      }
      .precinct-result-name {
        font-weight: 600;
        color: var(--primary, #1a365d);
        font-family: monospace;
      }
      .precinct-result-meta {
        font-size: 0.9em;
        color: #718096;
        margin-top: 2px;
      }
      .precinct-result-votes {
        display: flex;
        gap: 15px;
        margin-top: 4px;
        font-size: 0.85em;
      }
      .precinct-vote-dem {
        color: #2563eb;
      }
      .precinct-vote-rep {
        color: #dc2626;
      }
      .precinct-vote-total {
        color: #718096;
      }
      .precinct-margin {
        font-weight: 500;
      }
      .precinct-margin.dem-lead {
        color: #2563eb;
      }
      .precinct-margin.rep-lead {
        color: #dc2626;
      }
      .precinct-message {
        padding: 20px;
        text-align: center;
        color: #718096;
      }
      .precinct-error {
        color: #c53030;
        background: #fff5f5;
        border-radius: 6px;
      }
      .precinct-loading {
        text-align: center;
        padding: 40px;
        color: #718096;
      }
      .precinct-loading::after {
        content: '';
        display: inline-block;
        width: 20px;
        height: 20px;
        border: 2px solid #e2e8f0;
        border-top-color: var(--secondary, #2c5282);
        border-radius: 50%;
        animation: precinct-spin 0.8s linear infinite;
        margin-left: 10px;
        vertical-align: middle;
      }
      @keyframes precinct-spin {
        to { transform: rotate(360deg); }
      }
      .precinct-pagination {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 8px;
        margin-top: 20px;
        padding-top: 20px;
        border-top: 1px solid var(--border, #e2e8f0);
        flex-wrap: wrap;
      }
      .precinct-pagination-info {
        width: 100%;
        text-align: center;
        color: #718096;
        font-size: 0.9em;
        margin-bottom: 10px;
      }
      .precinct-pagination button {
        padding: 8px 12px;
        border: 1px solid var(--border, #e2e8f0);
        background: white;
        border-radius: 4px;
        cursor: pointer;
        font-size: 0.9em;
        min-width: 40px;
      }
      .precinct-pagination button:hover:not(:disabled) {
        background: var(--bg, #f7fafc);
        border-color: var(--secondary, #2c5282);
      }
      .precinct-pagination button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      .precinct-pagination button.active {
        background: var(--secondary, #2c5282);
        color: white;
        border-color: var(--secondary, #2c5282);
      }
      .precinct-index-hidden {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  private insertUI(): void {
    const container = document.createElement('div');
    container.className = 'precinct-container';

    const stateOptions = this.states
      .map(s => `<option value="${s.state}">${s.name}</option>`)
      .join('');

    container.innerHTML = `
      <div class="precinct-filters">
        <label for="precinct-state-select">State:</label>
        <select id="precinct-state-select">
          <option value="">All States</option>
          ${stateOptions}
        </select>
        <label for="precinct-county-select">County:</label>
        <select id="precinct-county-select" disabled>
          <option value="">All Counties</option>
        </select>
        <form class="precinct-search-form" id="precinct-search-form">
          <input type="text" id="precinct-search-input" placeholder="Search by GEOID..." autocomplete="off">
          <button type="submit">Search</button>
        </form>
      </div>
      <div class="precinct-results" id="precinct-results">
        <div class="precinct-results-header">
          <h3 id="precinct-results-title">All Precincts</h3>
          <button type="button" class="precinct-clear" id="precinct-clear" style="display: none;">Clear Filters</button>
        </div>
        <div id="precinct-results-list"></div>
        <div id="precinct-pagination"></div>
      </div>
    `;

    const listView = document.getElementById('list-view');
    if (listView) {
      listView.appendChild(container);
    } else {
      const insertPoint = belowHeaderInsertPoint();
      if (insertPoint?.parentNode) {
        insertPoint.parentNode.insertBefore(container, insertPoint);
      }
    }

    // Hide any existing static content
    document.querySelectorAll('.alphabetical-index, section > ul, main > section').forEach(el => {
      el.classList.add('precinct-index-hidden');
    });

    // Event listeners
    document.getElementById('precinct-state-select')?.addEventListener('change', (e) => {
      const select = e.target as HTMLSelectElement;
      this.loadCounties(select.value);
      this.loadPrecincts({ state: select.value, county: '', query: this.currentQuery, page: 1 });
    });

    document.getElementById('precinct-county-select')?.addEventListener('change', (e) => {
      const select = e.target as HTMLSelectElement;
      this.loadPrecincts({ state: this.currentState, county: select.value, query: this.currentQuery, page: 1 });
    });

    document.getElementById('precinct-search-form')?.addEventListener('submit', (e) => {
      e.preventDefault();
      const input = document.getElementById('precinct-search-input') as HTMLInputElement;
      this.loadPrecincts({ state: this.currentState, county: this.currentCounty, query: input?.value.trim() || '', page: 1 });
    });

    document.getElementById('precinct-clear')?.addEventListener('click', () => {
      const stateSelect = document.getElementById('precinct-state-select') as HTMLSelectElement;
      const countySelect = document.getElementById('precinct-county-select') as HTMLSelectElement;
      const searchInput = document.getElementById('precinct-search-input') as HTMLInputElement;
      if (stateSelect) stateSelect.value = '';
      if (countySelect) { countySelect.value = ''; countySelect.innerHTML = '<option value="">All Counties</option>'; countySelect.disabled = true; }
      if (searchInput) searchInput.value = '';
      this.counties = [];
      this.loadPrecincts({ state: '', county: '', query: '', page: 1 });
    });

    window.addEventListener('popstate', () => this.loadFromURL());
  }

  private async loadFromURL(): Promise<void> {
    const urlParams = new URLSearchParams(window.location.search);
    const state = urlParams.get('state') || '';
    const county = urlParams.get('county') || '';
    const query = urlParams.get('q') || '';
    const page = parseInt(urlParams.get('page') || '1', 10) || 1;

    const stateSelect = document.getElementById('precinct-state-select') as HTMLSelectElement;
    const searchInput = document.getElementById('precinct-search-input') as HTMLInputElement;
    if (stateSelect) stateSelect.value = state;
    if (searchInput) searchInput.value = query;

    if (state) {
      await this.loadCounties(state);
      const countySelect = document.getElementById('precinct-county-select') as HTMLSelectElement;
      if (countySelect && county) countySelect.value = county;
    }

    this.loadPrecincts({ state, county, query, page, updateHistory: false });
  }

  private async loadPrecincts(options: { state?: string; county?: string; query?: string; page?: number; updateHistory?: boolean } = {}): Promise<void> {
    const state = options.state ?? this.currentState;
    const county = options.county ?? this.currentCounty;
    const query = options.query ?? this.currentQuery;
    const page = options.page || 1;
    const updateHistory = options.updateHistory !== false;

    const resultsList = document.getElementById('precinct-results-list');
    const paginationContainer = document.getElementById('precinct-pagination');
    const resultsTitle = document.getElementById('precinct-results-title');
    const clearButton = document.getElementById('precinct-clear');

    this.currentState = state;
    this.currentCounty = county;
    this.currentQuery = query;
    this.currentPage = page;

    if (resultsList) resultsList.innerHTML = '<div class="precinct-loading">Loading</div>';
    if (paginationContainer) paginationContainer.innerHTML = '';

    // Update title
    let title = 'All Precincts';
    const countyName = county ? this.getCountyName(county) : '';
    if (state && county && query) {
      title = `GEOID "${query}" in ${countyName} County, ${this.getStateName(state)}`;
    } else if (state && county) {
      title = `${countyName} County, ${this.getStateName(state)} Precincts`;
    } else if (state && query) {
      title = `GEOID "${query}" in ${this.getStateName(state)}`;
    } else if (state) {
      title = `${this.getStateName(state)} Precincts`;
    } else if (query) {
      title = `GEOID: "${query}"`;
    }
    if (resultsTitle) resultsTitle.textContent = title;
    if (clearButton) clearButton.style.display = (state || county || query) ? 'block' : 'none';

    if (updateHistory) {
      this.updateURL(state, county, query, page);
    }

    const graphqlQuery = this.buildGraphQLQuery(state, county, query, page);

    try {
      const res = await fetch('/api/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(graphqlQuery)
      });

      if (!res.ok) {
        if (resultsList) resultsList.innerHTML = `<p class="precinct-message precinct-error">Failed to load: ${res.status}</p>`;
        return;
      }

      const data: PctGraphQLResponse = await res.json();

      if (data.errors) {
        if (resultsList) resultsList.innerHTML = `<p class="precinct-message precinct-error">Error: ${data.errors[0]?.message || 'GraphQL error'}</p>`;
        return;
      }

      const precincts = data.data?.precincts || [];
      this.totalResults = data.data?.precinctsConnection?.totalCount || precincts.length;

      this.displayResults(precincts);
      this.displayPagination(page, this.totalResults);

    } catch (err) {
      if (resultsList) resultsList.innerHTML = `<p class="precinct-message precinct-error">Error: ${(err as Error).message}</p>`;
    }
  }

  private buildGraphQLQuery(state: string, county: string, searchQuery: string, page: number): GraphQLRequest {
    return {
      query: PRECINCTS_QUERY,
      variables: {
        offset: (page - 1) * PCT_PAGE_SIZE,
        ...(state && { state }),
        ...(county && { county }),
        ...(searchQuery && { search: searchQuery })
      }
    };
  }

  /** Convert string to URI-safe slug (matches PathResolver.makeUriSafe) */
  private makeUriSafe(s: string | undefined): string {
    if (!s) return '';
    return s.toLowerCase()
      .replace(/[\s/]+/g, '-')
      .replace(/[^a-z0-9._-]/g, '');
  }

  private displayResults(precincts: PctPrecinctResult[]): void {
    const resultsList = document.getElementById('precinct-results-list');
    if (!resultsList) return;

    if (!precincts.length) {
      resultsList.innerHTML = '<p class="precinct-message">No precincts found.</p>';
      return;
    }

    const html = precincts.map(precinct => {
      const geoid = precinct.geoid || '';
      const state = precinct.state || '';
      const stateName = this.getStateName(state);
      const precinctId = precinct.precinct_id || '';

      const url = `/${canonicalPath('Precinct', precinct.id)}/`;

      // Format county info
      const countyFips = precinct.county_fips || '';
      const county = this.counties.find(c => countyGeoid(c) === countyFips);
      const countyLabel = county ? `${county.name} County, ` : '';

      // Format vote counts
      const votesDem = precinct.votes_dem ?? 0;
      const votesRep = precinct.votes_rep ?? 0;
      const votesTotal = precinct.votes_total ?? 0;
      const margin = precinct.pct_dem_lead ?? 0;

      // Determine margin class and text
      const marginClass = margin > 0 ? 'dem-lead' : margin < 0 ? 'rep-lead' : '';
      const marginText = margin !== 0
        ? `${margin > 0 ? 'D' : 'R'}+${Math.abs(margin * 100).toFixed(1)}%`
        : 'Tie';

      return `
        <a href="${url}" class="precinct-result">
          <div class="precinct-result-name">${this.escapeHtml(geoid)}</div>
          <div class="precinct-result-meta">${this.escapeHtml(countyLabel + stateName)}</div>
          <div class="precinct-result-votes">
            <span class="precinct-vote-dem">D: ${votesDem.toLocaleString()}</span>
            <span class="precinct-vote-rep">R: ${votesRep.toLocaleString()}</span>
            <span class="precinct-vote-total">Total: ${votesTotal.toLocaleString()}</span>
            <span class="precinct-margin ${marginClass}">${marginText}</span>
          </div>
        </a>
      `;
    }).join('');

    resultsList.innerHTML = html;
  }

  private displayPagination(page: number, total: number): void {
    const paginationContainer = document.getElementById('precinct-pagination');
    if (!paginationContainer) return;

    const totalPages = Math.ceil(total / PCT_PAGE_SIZE);

    if (totalPages <= 1) {
      paginationContainer.innerHTML = total > 0
        ? `<div class="precinct-pagination-info">Showing all ${total.toLocaleString()} precincts</div>`
        : '';
      return;
    }

    const start = (page - 1) * PCT_PAGE_SIZE + 1;
    const end = Math.min(page * PCT_PAGE_SIZE, total);

    let html = `<div class="precinct-pagination-info">Showing ${start.toLocaleString()}-${end.toLocaleString()} of ${total.toLocaleString()} precincts</div>`;
    html += '<div class="precinct-pagination">';
    html += `<button type="button" data-page="1" ${page === 1 ? 'disabled' : ''}>First</button>`;
    html += `<button type="button" data-page="${page - 1}" ${page === 1 ? 'disabled' : ''}>Prev</button>`;

    const pageNumbers = this.getPageNumbers(page, totalPages);
    let lastNum = 0;

    for (const num of pageNumbers) {
      if (num - lastNum > 1) {
        html += '<span style="padding: 8px 4px; color: #718096;">...</span>';
      }
      html += `<button type="button" data-page="${num}" class="${num === page ? 'active' : ''}">${num}</button>`;
      lastNum = num;
    }

    html += `<button type="button" data-page="${page + 1}" ${page === totalPages ? 'disabled' : ''}>Next</button>`;
    html += `<button type="button" data-page="${totalPages}" ${page === totalPages ? 'disabled' : ''}>Last</button>`;
    html += '</div>';

    paginationContainer.innerHTML = html;

    paginationContainer.querySelectorAll('button[data-page]').forEach(btn => {
      btn.addEventListener('click', () => {
        const targetPage = parseInt((btn as HTMLButtonElement).dataset.page || '1', 10);
        if (targetPage >= 1 && targetPage <= totalPages && targetPage !== page) {
          this.loadPrecincts({ page: targetPage });
          document.getElementById('precinct-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  private getPageNumbers(current: number, total: number): number[] {
    const pages: number[] = [];
    const delta = 2;

    pages.push(1);
    for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
      pages.push(i);
    }
    if (total > 1) pages.push(total);

    return [...new Set(pages)].sort((a, b) => a - b);
  }

  private updateURL(state: string, county: string, query: string, page: number): void {
    const url = new URL(window.location.href);

    if (state) {
      url.searchParams.set('state', state);
    } else {
      url.searchParams.delete('state');
    }

    if (county) {
      url.searchParams.set('county', county);
    } else {
      url.searchParams.delete('county');
    }

    if (query) {
      url.searchParams.set('q', query);
    } else {
      url.searchParams.delete('q');
    }

    if (page > 1) {
      url.searchParams.set('page', String(page));
    } else {
      url.searchParams.delete('page');
    }

    window.history.pushState({ state, county, query, page }, '', url.toString());
  }

  private escapeHtml(str: string | undefined): string {
    if (!str) return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

// Auto-initialize
new PrecinctIndex();
